import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const fmtUSD = (n) => "$" + Math.round(n).toLocaleString();

export function generateReport(merchantName, analysis, accounts) {
  const doc = new jsPDF();
  const { monthly, calMonth1, calMonth2, calMonth3, activeLenders, dailyFactorTotal, currentBalance, balanceAlerts, settings } = analysis;

  const loanObligationPct = (monthly.loanPayments / (monthly.totalCredits || 1)) * 100;
  const balanceCoverage = currentBalance / (dailyFactorTotal || 1);
  const riskScore = Math.min(100, Math.round(
    (balanceCoverage < (settings.coverage_critical_days || 3) ? 40 : balanceCoverage < 7 ? 20 : 0) +
    (loanObligationPct > (settings.stacking_warning_pct || 35) ? 30 : loanObligationPct > 20 ? 15 : 0) +
    (activeLenders.length >= (settings.stacking_high_positions || 3) ? 30 : activeLenders.length >= 2 ? 15 : 0)
  ));
  const riskLabel = riskScore >= 60 ? "HIGH" : riskScore >= 30 ? "MODERATE" : "LOW";
  const today = new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });

  let y = 20;

  // ── Header ──────────────────────────────────────────────────────
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("MCA Bank Analysis Report", 14, y);
  y += 8;
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100);
  doc.text(`Generated ${today}`, 14, y);
  y += 12;

  // ── Merchant Info ───────────────────────────────────────────────
  doc.setTextColor(0);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Merchant: " + merchantName, 14, y);
  y += 6;
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(80);
  if (accounts[0]) doc.text(`Account: ${accounts[0].name} (${accounts[0].type})`, 14, y);
  y += 10;

  // ── Risk Score Box ──────────────────────────────────────────────
  const riskColor = riskScore >= 60 ? [220, 50, 50] : riskScore >= 30 ? [200, 150, 0] : [0, 150, 80];
  doc.setFillColor(...riskColor);
  doc.roundedRect(14, y, 55, 22, 3, 3, "F");
  doc.setTextColor(255);
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(`${riskScore}`, 22, y + 14);
  doc.setFontSize(9);
  doc.text(`${riskLabel} RISK`, 42, y + 14);

  // Key Metrics beside risk box
  doc.setTextColor(0);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  const metricsX = 80;
  doc.text(`Current Balance: ${fmtUSD(currentBalance)}`, metricsX, y + 6);
  doc.text(`Monthly Revenue: ${fmtUSD(monthly.totalCredits)}`, metricsX, y + 12);
  doc.text(`Daily Factor Drain: ${fmtUSD(dailyFactorTotal)}/day`, metricsX, y + 18);

  doc.text(`Active Positions: ${activeLenders.length}`, 145, y + 6);
  doc.text(`Balance Coverage: ${Math.round(balanceCoverage)} days`, 145, y + 12);
  doc.text(`Obligation Ratio: ${loanObligationPct.toFixed(1)}%`, 145, y + 18);
  y += 30;

  // ── Alerts ──────────────────────────────────────────────────────
  if (balanceAlerts.length > 0) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(180, 40, 40);
    doc.text("Active Alerts", 14, y);
    y += 5;
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    for (const alert of balanceAlerts) {
      const prefix = alert.level === "CRITICAL" ? "CRITICAL: " : "WARNING: ";
      doc.text(prefix + alert.msg, 14, y);
      y += 4;
    }
    y += 4;
  }

  // ── Period Summary Table ────────────────────────────────────────
  doc.setTextColor(0);
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Period Summary", 14, y);
  y += 2;

  autoTable(doc, {
    startY: y,
    head: [["Metric", "Rolling 30d", calMonth1.label, calMonth2.label, calMonth3.label]],
    body: [
      ["Revenue Credits", fmtUSD(monthly.totalCredits), fmtUSD(calMonth1.totalCredits), fmtUSD(calMonth2.totalCredits), fmtUSD(calMonth3.totalCredits)],
      ["Operating Debits", fmtUSD(monthly.totalDebits), fmtUSD(calMonth1.totalDebits), fmtUSD(calMonth2.totalDebits), fmtUSD(calMonth3.totalDebits)],
      ["Loan Repayments", fmtUSD(monthly.loanPayments), fmtUSD(calMonth1.loanPayments), fmtUSD(calMonth2.loanPayments), fmtUSD(calMonth3.loanPayments)],
      ["Loan Proceeds", fmtUSD(monthly.loanProceeds), fmtUSD(calMonth1.loanProceeds), fmtUSD(calMonth2.loanProceeds), fmtUSD(calMonth3.loanProceeds)],
      [
        "Net Cash Flow",
        fmtUSD(monthly.totalCredits - monthly.totalDebits - monthly.loanPayments),
        fmtUSD(calMonth1.totalCredits - calMonth1.totalDebits - calMonth1.loanPayments),
        fmtUSD(calMonth2.totalCredits - calMonth2.totalDebits - calMonth2.loanPayments),
        fmtUSD(calMonth3.totalCredits - calMonth3.totalDebits - calMonth3.loanPayments),
      ],
      ["Avg Balance", fmtUSD(monthly.avgDailyBalance), fmtUSD(calMonth1.avgDailyBalance), fmtUSD(calMonth2.avgDailyBalance), fmtUSD(calMonth3.avgDailyBalance)],
      ["Min Balance", fmtUSD(monthly.minBalance), fmtUSD(calMonth1.minBalance), fmtUSD(calMonth2.minBalance), fmtUSD(calMonth3.minBalance)],
    ],
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [40, 40, 50], textColor: [255, 255, 255], fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 245, 250] },
    margin: { left: 14 },
  });

  y = doc.lastAutoTable.finalY + 8;

  // ── Active Positions Table ──────────────────────────────────────
  if (activeLenders.length > 0) {
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text("Detected Advance Positions", 14, y);
    y += 2;

    autoTable(doc, {
      startY: y,
      head: [["Lender", "Status"]],
      body: activeLenders.map((l) => [l, "ACTIVE"]),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [150, 50, 50], textColor: [255, 255, 255], fontStyle: "bold" },
      margin: { left: 14 },
      tableWidth: 100,
    });

    y = doc.lastAutoTable.finalY + 8;
  }

  // ── Risk Assessment ─────────────────────────────────────────────
  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Risk Assessment", 14, y);
  y += 2;

  autoTable(doc, {
    startY: y,
    head: [["Check", "Result", "Details"]],
    body: [
      [
        "Balance Coverage",
        balanceCoverage < (settings.coverage_critical_days || 3) ? "FAIL" : "PASS",
        `${Math.round(balanceCoverage)} days of factor payments covered`,
      ],
      [
        "Obligation Ratio",
        loanObligationPct > (settings.stacking_warning_pct || 35) ? "FAIL" : "PASS",
        `${loanObligationPct.toFixed(1)}% of revenue to loan repayments`,
      ],
      [
        "Position Stacking",
        activeLenders.length >= (settings.stacking_high_positions || 3) ? "FAIL" : "PASS",
        `${activeLenders.length} active position(s)`,
      ],
    ],
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [40, 40, 50], textColor: [255, 255, 255], fontStyle: "bold" },
    didParseCell: (data) => {
      if (data.column.index === 1 && data.section === "body") {
        data.cell.styles.textColor = data.cell.raw === "FAIL" ? [200, 40, 40] : [0, 140, 60];
        data.cell.styles.fontStyle = "bold";
      }
    },
    margin: { left: 14 },
  });

  // ── Footer ──────────────────────────────────────────────────────
  const pageH = doc.internal.pageSize.getHeight();
  doc.setFontSize(7);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(150);
  doc.text("Generated by MCA Bank Analyzer — Confidential", 14, pageH - 10);
  doc.text(`Analysis period: ${analysis.daily.txnCount > 0 ? "Last 120 days" : "No data"}`, 14, pageH - 6);

  // ── Save ────────────────────────────────────────────────────────
  const safeName = merchantName.replace(/[^a-zA-Z0-9 ]/g, "").replace(/\s+/g, "_");
  doc.save(`MCA_Report_${safeName}_${new Date().toISOString().split("T")[0]}.pdf`);
}
